import { Box, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Search as SearchInputIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { ReactComponent as SearchIcon } from '../../assets/svg/search.svg';
import { api } from '../../shared/service/dataprovider';
import { useSelectedProperty } from '../../shared/useSelectedProperty';
import tokens from '../../shared/theme/tokens';
import Pagination from '../../components/pagination';
import PageHeader from '../../components/PageHeader';
import Panel from '../../components/Panel';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';

const ALL_UNITS = '__all__';

export default function Properties() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get('/v1/admin/properties'),
  });
  const searchQuery = queryClient.getQueryData(['properties/search']);

  const [leases, setLeases] = useState([]);
  const [totalLeases, setTotalLeases] = useState(0);
  const [isFetchingLeases, setFetchingLeases] = useState(false);
  // Search Status
  const [selectedProperty, setSelectedProperty] = useSelectedProperty();
  const [selectedUnit, setSelectedUnit] = useState(ALL_UNITS);
  const [leaseStatus, setLeaseStatus] = useState('');
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');

  // Pagination Status
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    if (searchQuery) {
      // Property comes from the shared cross-page selection; only the unit/status/
      // name search state is page-local and restored from the saved search.
      setSelectedUnit(searchQuery.selectedUnit || ALL_UNITS);
      setLeaseStatus(searchQuery.leaseStatus);
      setName(searchQuery.name);
      setSearch(searchQuery.name);
    }
    if (data && data.data && data.data.length && !selectedProperty) {
      setSelectedProperty(data.data[0].id);
    }
  }, [selectedProperty, data, searchQuery, setSelectedProperty]);

  const filterKey = `${selectedProperty}|${selectedUnit}|${leaseStatus}|${name}`;
  const filterKeyRef = useRef(filterKey);

  useEffect(() => {
    if (!selectedProperty) return;
    const filtersChanged = filterKeyRef.current !== filterKey;
    if (filtersChanged) {
      filterKeyRef.current = filterKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    if (!(selectedUnit || name)) {
      setLeases([]);
      setTotalLeases(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setFetchingLeases(true);
      try {
        const leaseResp = await api.get('/v1/admin/lease', {
          params: {
            property_id: selectedProperty,
            unit: selectedUnit && selectedUnit !== ALL_UNITS ? selectedUnit : undefined,
            status: leaseStatus || undefined,
            name: name || undefined,
            page: currentPage,
            pageSize: rowsPerPage,
          },
        });
        if (!cancelled) {
          const body = leaseResp.data;
          if (Array.isArray(body)) {
            const size = Number(rowsPerPage);
            const start = size * (currentPage - 1);
            setLeases(body.slice(start, start + size));
            setTotalLeases(body.length);
          } else {
            setLeases(body.leases || []);
            setTotalLeases(body.total || 0);
          }
        }
      } finally {
        if (!cancelled) setFetchingLeases(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterKey, selectedProperty, selectedUnit, leaseStatus, name, currentPage, rowsPerPage]);

  const units = useMemo(() => {
    return data?.data.find(d => d.id === selectedProperty)?.units?.sort((u1, u2) => u1.unitNumber.localeCompare(u2.unitNumber));
  }, [data, selectedProperty]);

  const handleSearchChange = n => {
    setName(n);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleSearchChange, 300);
  }, []);

  const openLease = row => {
    queryClient.setQueryData(['properties/search'], {
      selectedProperty,
      selectedUnit,
      leaseStatus,
      name,
    });
    navigate(`/lease/${row.id}`);
  };

  const columns = [
    { key: 'Unit', header: t('properties.unitNumber') },
    {
      key: 'residents',
      header: t('properties.residentName'),
      render: row => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {row.leaseLinks.map(ll => (
            <Typography
              key={ll.id}
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: tokens.colors.primaryDeep,
                backgroundColor: tokens.colors.primarySoft,
                borderRadius: `${tokens.radius.pill}px`,
                px: 1.25,
                py: '3px',
                whiteSpace: 'nowrap',
              }}
            >
              {row.leaseLinks.length > 1 ? ll.people.FirstName + ' ' + ll.people.LastName.substring(0, 1) : ll.people.FirstName + ' ' + ll.people.LastName}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: 'Status',
      header: t('properties.status'),
      render: row => <StatusChip status={row.Status} />,
    },
    {
      key: 'ssn',
      header: t('properties.ssnVerified'),
      render: row => `${row.leaseLinks.filter(ll => ll.people.kycStatus === 'Success').length}/${row.leaseLinks.length}`,
    },
    {
      key: 'registered',
      header: t('properties.registered'),
      render: row => `${row.leaseLinks.filter(ll => ll.people.user).length}/${row.leaseLinks.length}`,
    },
    { key: 'start', header: t('properties.startDate'), render: row => moment(row.LeaseStartDate).format('MM/DD/YYYY') },
    { key: 'end', header: t('properties.endDate'), render: row => moment(row.LeaseEndDate).format('MM/DD/YYYY') },
  ];

  const renderLeaseTable = () => {
    if (!selectedUnit && !name) {
      return (
        <Panel padding={0}>
          <Box
            sx={{
              width: '100%',
              height: '226px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
              <SearchIcon />
              <Typography sx={{ fontSize: '15px', color: 'text.secondary' }}>{t('properties.search')}</Typography>
            </Box>
          </Box>
        </Panel>
      );
    }

    return (
      <DataTable
        columns={columns}
        rows={leases}
        rowKey={row => row.id}
        onRowClick={openLease}
        loading={isFetchingLeases}
        emptyContent={selectedUnit && selectedUnit !== ALL_UNITS ? t('properties.emptySearchWithUnit', { unit: selectedUnit }) : t('properties.empty')}
        footer={
          totalLeases > 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Pagination
                currentPage={currentPage}
                totalPage={Math.ceil(totalLeases / rowsPerPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={p => {
                  setCurrentPage(1);
                  setRowsPerPage(p);
                }}
                onChangeCurrentPage={p => setCurrentPage(p)}
              />
            </Box>
          ) : null
        }
      />
    );
  };

  return (
    <Box>
      <PageHeader
        title={t('properties.title')}
        action={
          <Select
            size="small"
            value={selectedProperty}
            onChange={e => {
              setSelectedProperty(e.target.value);
              setSelectedUnit(ALL_UNITS);
              setCurrentPage(1);
              setLeaseStatus('');
              queryClient.setQueryData(['properties/search'], '');
              setLeases([]);
              setTotalLeases(0);
            }}
            sx={{ minWidth: 260, backgroundColor: 'background.paper' }}
          >
            {data?.data.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        }
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            if (e.target.value === '' || e.target.value.length > 2) {
              debouncedResults(e.target.value);
            }
          }}
          placeholder="Search for resident name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchInputIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: '312px', backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <Select
          size="small"
          displayEmpty
          value={selectedUnit}
          onChange={e => {
            setSelectedUnit(e.target.value);
            setLeaseStatus('');
          }}
          sx={{ minWidth: '200px', backgroundColor: 'background.paper' }}
          renderValue={selected => {
            if (!selected) {
              return <>{t('properties.unitNumber')}</>;
            }
            if (selected === ALL_UNITS) {
              return <>All units</>;
            }
            return selected;
          }}
        >
          <MenuItem key="Blank" value={ALL_UNITS}>
            All units
          </MenuItem>
          {units?.map(u => (
            <MenuItem key={u.id} value={u.unitNumber}>
              {u.unitNumber}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          displayEmpty
          value={leaseStatus}
          onChange={e => {
            setLeaseStatus(e.target.value);
          }}
          sx={{ minWidth: '200px', backgroundColor: 'background.paper' }}
          renderValue={selected => {
            if (!selected) {
              return <>All Status</>;
            }
            return selected;
          }}
        >
          <MenuItem key="Blank" value="">
            All
          </MenuItem>
          <MenuItem key="Current" value="Current">
            {t('properties.current')}
          </MenuItem>
          <MenuItem key="Pending" value="Pending">
            {t('properties.pending')}
          </MenuItem>
          <MenuItem key="Pending Transfer" value="Pending Transfer">
            {t('properties.pendingTransfer')}
          </MenuItem>
          <MenuItem key="Former" value="Former">
            {t('properties.former')}
          </MenuItem>
          <MenuItem key="Evicted" value="Evicted">
            {t('properties.evicted')}
          </MenuItem>
          <MenuItem key="Under Eviction" value="Under Eviction">
            {t('properties.underEviction')}
          </MenuItem>
        </Select>
      </Box>
      {renderLeaseTable()}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        TP = TenantPort
      </Typography>
    </Box>
  );
}
